import React from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: 450px;
  margin: 20px 0;
  transform: rotate(180deg);
`;

const COLORS = [
  '#0088FE', // blue
  '#00C49F', // green
  '#FFBB28', // yellow
  '#FF8042', // orange
  '#A28CFF', // purple
  '#FF6699', // pink
  '#00BFFF', // light blue
  '#FF1493', // deep pink
  '#32CD32', // lime green
  '#9370DB', // medium purple
  '#20B2AA', // light sea green
  '#FF4500', // orange red
  '#4169E1', // royal blue
  '#FFD700', // gold
  '#8B4513', // saddle brown
  '#FF69B4', // hot pink
  '#4B0082', // indigo
  '#00FF7F', // spring green
  '#CD853F', // peru
  '#48D1CC', // medium turquoise
  '#C71585', // medium violet red
  '#F4A460', // sandy brown
  '#6B8E23', // olive drab
  '#BA55D3', // medium orchid
  '#B8860B', // dark goldenrod
  '#FF00FF', // magenta
  '#556B2F', // dark olive green
  '#8A2BE2', // blue violet
  '#2F4F4F', // dark slate gray
  '#FF7F50', // coral
];

// Додаємо глобальний стиль для підписів сегментів пирога
const PieLabelStyle = styled.style`
  .recharts-pie-label-text {
    fill: #222 !important;
    font-weight: 500;
  }
`;

// Кастомний label renderer для зовнішніх підписів з лінією до краю тексту
const renderCustomLabel = ({
  cx, cy, midAngle, outerRadius, percent, segment,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const isRightSide = x > cx;
  const dx = isRightSide ? 8 : -8;
  const textAnchor = isRightSide ? 'end' : 'start';
  const lineEndX = x + dx;
  const lineEndY = y;
  const lineStartX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
  const lineStartY = cy + outerRadius * Math.sin(-midAngle * RADIAN);

  return (
    <g>
      <polyline
        points={`${lineStartX},${lineStartY} ${x},${y} ${lineEndX},${lineEndY}`}
        stroke="#222"
        fill="none"
      />
      <text
        x={lineEndX}
        y={lineEndY}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fill="#222"
        fontWeight={500}
      >
        {`${segment} (${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

renderCustomLabel.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  midAngle: PropTypes.number,
  outerRadius: PropTypes.number,
  percent: PropTypes.number,
  segment: PropTypes.string,
};

renderCustomLabel.defaultProps = {
  cx: 0,
  cy: 0,
  midAngle: 0,
  outerRadius: 0,
  percent: 0,
  segment: '',
};

const PieChartMessage = ({ data }) => {
  if (!data || !data[0] || !data[0].data) {
    return null;
  }

  const { data: chartData, chartConfig, config } = data[0].data;

  // Check if chartData exists and has elements
  if (!chartData || chartData.length === 0) {
    return <div>No data for pie chart</div>;
  }

  return (
    <>
      <style>
        {'.recharts-pie-label-text { fill: #222 !important; font-weight: 500; }'}
      </style>
      <PieLabelStyle />
      <ChartContainer>
        {/* Chart title */}
        <h3 style={{ textAlign: 'center', marginBottom: '10px' }}>{(config && config.title) ? config.title : 'Pie Chart'}</h3>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="segment"
              cx="50%"
              cy="50%"
              outerRadius={150}
              label={renderCustomLabel}
              labelLine={false}
            >
              {chartData.map((entry, idx) => (
                <Cell
                  key={`cell-${entry.segment}`}
                  fill={entry.color || COLORS[idx % COLORS.length]}
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={value => [value, (chartConfig && chartConfig.value && chartConfig.value.label) ? chartConfig.value.label : '']}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              wrapperStyle={{
                paddingTop: '24px',
                marginBottom: '16px',
                fontSize: '1rem',
                color: '#222',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
      <style>
        {`
          .recharts-legend-wrapper {
            width: 100%;
            overflow-x: auto;
            white-space: nowrap;
            display: flex !important;
            justify-content: center;
          }
          .recharts-legend-item {
            display: inline-flex !important;
            align-items: center;
            gap: 4px;
            margin-right: 16px;
          }
        `}
      </style>
    </>
  );
};

PieChartMessage.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.shape({
        data: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.number.isRequired,
            segment: PropTypes.string.isRequired,
          }),
        ).isRequired,
        config: PropTypes.shape({
          title: PropTypes.string.isRequired,
          xAxisKey: PropTypes.string.isRequired,
          totalLabel: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        }).isRequired,
        chartConfig: PropTypes.shape({
          value: PropTypes.shape({
            color: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
    }),
  ).isRequired,
};

export default PieChartMessage;
